import { IS_PRODUCTION, SERVICE_URL } from "@app/config/getServiceUrl";
import { redactSearchQueryString, redactSearchUrlsDeep } from "@app/config/loggers/redactSearchUrl";
import { HTTP_STATUS } from "@base/lib/error";
import { isAxiosApiError } from "@base/lib/utils";
import * as Sentry from "@sentry/react";
import { type AxiosError, isAxiosError } from "axios";

/**
 * Sentry(및 Query/Mutation 전역 onError)에 올리지 않을 “예상 가능한” API 오류 패턴.
 * 여기에만 항목을 추가·수정하면 된다.
 */
export type ExpectedApiErrorRule = {
  /** Axios HTTP 응답 코드 (`response.status`) */
  status: number;
  /** 있으면 ApiError 본문의 `data.code`까지 일치해야 매칭된다. */
  code?: string;
};

export const SENTRY_EXPECTED_API_ERRORS: ExpectedApiErrorRule[] = [
  { status: HTTP_STATUS.UNAUTHORIZED },
  { status: HTTP_STATUS.FORBIDDEN },
  // 일일 로그 드래프트 데이터가 서버에 없을 때 (로컬에만 존재하는 케이스)
  {
    status: HTTP_STATUS.BAD_REQUEST,
    code: "NONEXISTENT_DRAFT_DATA",
  },
  // 최근 일일 로그가 없을 때
  {
    status: HTTP_STATUS.BAD_REQUEST,
    code: "NO_RECENT_DAILY_LOG",
  },
  // 로컬 드래프트, 서버 드래프트 version 충돌할 때 409 에러
  {
    status: HTTP_STATUS.CONFLICT,
    code: "VERSION_CONFLICT",
  },
];

function matchesExpectedApiError(error: AxiosError, rule: ExpectedApiErrorRule): boolean {
  const { status, code } = rule;
  const responseStatus = error.response?.status;

  if (responseStatus === status) {
    if (code === undefined) {
      return true;
    }
    return isAxiosApiError(error) && error.response.data.code === code;
  }

  return false;
}

/**
 * 브라우저/서드파티에서 발생하는 노이즈 에러.
 * 앱 로직과 무관하며 대응할 수 없는 이벤트이므로 Sentry에 보내지 않는다.
 */
const IGNORED_ERRORS: Array<string | RegExp> = [
  "ResizeObserver loop completed with undelivered notifications",
  "ResizeObserver loop limit exceeded",
  "Non-Error promise rejection captured",
  "Non-Error exception captured",
  /^Loading chunk .+ failed/,
  /^Loading CSS chunk .+ failed/,
  "ChunkLoadError",
  "Network Error",
  "Request aborted",
  "AbortError",
  "TypeError: Failed to fetch",
  "TypeError: NetworkError when attempting to fetch resource",
  "TypeError: cancelled",
];

const DENY_URLS: RegExp[] = [
  /extensions\//i,
  /^chrome:\/\//i,
  /^chrome-extension:\/\//i,
  /^moz-extension:\/\//i,
  /^safari-extension:\/\//i,
];

const SENTRY_DSN = "https://f6ec40c5ab23d1c8cbb99bf895c0a9dd@o209526.ingest.us.sentry.io/4511131628601344";

function truncate(value: string | undefined, maxLength: number): string {
  if (!value) return "";
  return value.length > maxLength ? `${value.slice(0, maxLength)}…` : value;
}

/**
 * 공사일지 검색어·cursor가 error·transaction·breadcrumb의 URL에 남지 않게 가린다.
 * 다른 필드와 다른 경로의 URL은 바꾸지 않는다.
 */
export function redactSearchFromSentryEvent<T extends Sentry.Event>(event: T): T {
  const redacted = redactSearchUrlsDeep(event);
  const request = redacted.request;
  if (request?.url && typeof request.query_string === "string") {
    request.query_string = redactSearchQueryString(request.url, request.query_string);
  }
  return redacted;
}

export function isExpectedApiError(error: unknown): boolean {
  if (!isAxiosError(error)) return false;
  return SENTRY_EXPECTED_API_ERRORS.some((rule) => matchesExpectedApiError(error, rule));
}

export function initSentry(): void {
  Sentry.init({
    dsn: SENTRY_DSN,
    enabled: import.meta.env.PROD,
    release: `bricks@${__BUILD_HASH__}`,
    environment: IS_PRODUCTION ? "production" : "development",
    sendDefaultPii: true,

    integrations: [
      Sentry.browserTracingIntegration(),
      ...(IS_PRODUCTION
        ? [
            Sentry.replayIntegration({
              maskAllText: true,
              blockAllMedia: false,
            }),
          ]
        : []),
    ],

    tracesSampleRate: 0.2,
    tracePropagationTargets: ["localhost", SERVICE_URL],

    // Replay는 할당량 소모가 크므로 실제 프로덕션 호스트에서만 수집한다.
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: IS_PRODUCTION ? 1.0 : 0,

    ignoreErrors: IGNORED_ERRORS,
    denyUrls: DENY_URLS,

    beforeBreadcrumb(breadcrumb) {
      return redactSearchUrlsDeep(breadcrumb);
    },

    beforeSendTransaction(event) {
      return redactSearchFromSentryEvent(event);
    },

    beforeSend(event, hint) {
      if (!navigator.onLine) return null;

      const error = hint?.originalException;

      if (isAxiosError(error)) {
        if (isExpectedApiError(error)) {
          return null;
        }

        const status = error.response?.status;

        if (status !== undefined && status >= 500) {
          event.contexts = {
            ...event.contexts,
            api: {
              url: error.config?.url,
              method: error.config?.method?.toUpperCase(),
              status,
              response_data: truncate(
                typeof error.response?.data === "string" ? error.response.data : JSON.stringify(error.response?.data),
                1000,
              ),
            },
          };
        }
      }

      return redactSearchFromSentryEvent(event);
    },
  });
}
